import { createSlice } from '@reduxjs/toolkit';
import axios from 'utils/axios';

/**
 * Apple Retention Messaging configuration for one application.
 *
 * Sets and rules live together because the two are read together: a rule points at a set, and a
 * set cannot be deleted while a rule points at it. Loading them separately per tab meant the
 * rule editor could offer a set the message list had already dropped.
 */
const initialState = {
  isLoading: false,
  /** Set when the last load failed, so a table can say so instead of rendering as empty. */
  error: null,
  /** True once a load has finished, which is how "empty" is told apart from "not yet". */
  loaded: false,
  /** One message in every language it exists in; each carries its own Apple state. */
  sets: [],
  /** Localisations belonging to no set, so they are not simply invisible. */
  unassigned: [],
  rules: [],
  /** Configured defaults, and the app's own Apple subscription products to lay the grid out. */
  defaults: [],
  products: [],
  images: [],
  /** In-flight write, so a form can disable its button without inventing local state. */
  isSaving: false,
  preview: { isLoading: false, result: null, error: null },
  /**
   * Whether Apple calls us: the URL it holds per environment, the latest performance test and the
   * next step. Read from Apple on every load, so it is kept apart from the configuration — a
   * slow or failing Apple call must not blank the message and rule tables.
   */
  realtime: { isLoading: false, loaded: false, data: null, error: null },
};

const base = (appId) => `/retention-messaging/${appId}`;

const slice = createSlice({
  name: 'retentionMessaging',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.isSaving = false;
      state.error = action.payload;
    },

    getConfigurationSuccess(state, action) {
      state.isLoading = false;
      state.loaded = true;
      state.error = null;
      state.sets = action.payload.sets;
      state.unassigned = action.payload.unassigned;
      state.rules = action.payload.rules;
      state.defaults = action.payload.defaults;
      state.products = action.payload.products;
      state.images = action.payload.images;
    },

    startSaving(state) {
      state.isSaving = true;
    },

    doneSaving(state) {
      state.isSaving = false;
    },

    startPreview(state) {
      state.preview = { isLoading: true, result: null, error: null };
    },

    previewSuccess(state, action) {
      state.preview = { isLoading: false, result: action.payload, error: null };
    },

    previewFailed(state, action) {
      state.preview = { isLoading: false, result: null, error: action.payload };
    },

    startRealtime(state) {
      state.realtime.isLoading = true;
      state.realtime.error = null;
    },

    realtimeSuccess(state, action) {
      state.realtime = { isLoading: false, loaded: true, data: action.payload, error: null };
    },

    realtimeFailed(state, action) {
      state.realtime = { ...state.realtime, isLoading: false, loaded: true, error: action.payload };
    },

    reset() {
      return initialState;
    },
  },
});

export default slice.reducer;
export const { reset } = slice.actions;

/**
 * The axios instance unwraps for us: its interceptor returns the payload on success and rejects
 * with the server's message as a plain string. Everything below reads the value directly, and
 * every catch handles a string rather than an Error.
 */
const messageOf = (error) => (typeof error === 'string' ? error : error?.message) || 'Request failed';

export function getConfiguration(appId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const [sets, rules, defaults, images] = await Promise.all([
        axios.get(`${base(appId)}/sets`),
        axios.get(`${base(appId)}/rules`),
        axios.get(`${base(appId)}/defaults`),
        axios.get(`${base(appId)}/images`),
      ]);
      dispatch(
        slice.actions.getConfigurationSuccess({
          sets: sets?.sets ?? [],
          unassigned: sets?.unassigned ?? [],
          rules: rules ?? [],
          defaults: defaults?.defaults ?? [],
          products: defaults?.products ?? [],
          images: images ?? [],
        }),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(messageOf(error)));
    }
  };
}

/**
 * Every write reloads both lists rather than patching state in place.
 *
 * Deleting a set can be refused because a rule points at it, and saving a rule changes what the
 * message list may offer — keeping the two in step by hand is the kind of thing that works until
 * it quietly does not.
 */
const writeThen = (appId, request) => async (dispatch) => {
  dispatch(slice.actions.startSaving());
  try {
    await request();
    dispatch(slice.actions.doneSaving());
    await dispatch(getConfiguration(appId));
    return null;
  } catch (error) {
    dispatch(slice.actions.doneSaving());
    return messageOf(error);
  }
};

export const createSet = (appId, name) =>
  writeThen(appId, () => axios.post(`${base(appId)}/sets`, { name }));

export const renameSet = (appId, setId, name) =>
  writeThen(appId, () => axios.put(`${base(appId)}/sets/${setId}`, { name }));

/** The languages survive and come back as unassigned rather than disappearing with the grouping. */
export const removeSet = (appId, setId) =>
  writeThen(appId, () => axios.delete(`${base(appId)}/sets/${setId}`));

/**
 * Sends every language in the set that is not at Apple yet for this environment.
 *
 * Not a transaction at Apple's end, so this can come back having sent three of five. The server
 * says which, and pressing again after a fix sends only what is still missing.
 */
export const uploadSet = (appId, setId, environment) =>
  writeThen(appId, () => axios.post(`${base(appId)}/sets/${setId}/upload`, { environment }));

export const saveMessage = (appId, messageId, payload) =>
  writeThen(appId, () =>
    messageId
      ? axios.put(`${base(appId)}/messages/${messageId}`, payload)
      : axios.post(`${base(appId)}/messages`, payload),
  );

export const removeMessage = (appId, messageId) =>
  writeThen(appId, () => axios.delete(`${base(appId)}/messages/${messageId}`));

/**
 * Sends a message to Apple. Sandbox approves automatically and says nothing about what
 * production will allow; production is a real review, and the text can never change afterwards.
 */
export const uploadMessage = (appId, messageId, environment) =>
  writeThen(appId, () =>
    axios.post(`${base(appId)}/messages/${messageId}/upload`, { environment }),
  );

/** Apple never tells us a verdict changed, so a panel only shows APPROVED if it asks. */
export const refreshMessageStates = (appId) =>
  writeThen(appId, () => axios.post(`${base(appId)}/messages/refresh`, {}));

export const saveRule = (appId, ruleId, payload) =>
  writeThen(appId, () =>
    ruleId
      ? axios.put(`${base(appId)}/rules/${ruleId}`, payload)
      : axios.post(`${base(appId)}/rules`, payload),
  );

/**
 * Names the message Apple falls back to for one product and locale.
 *
 * Idempotent at Apple's end, so this doubles as "change it" — unlike the message itself, which
 * can never be edited once uploaded.
 */
/**
 * Sends the PNG itself as the body — not a form.
 *
 * Apple wants exactly 3840 wide, 160-2160 tall and no transparency; the server checks all three
 * before Apple does, because Apple's only answer is "invalid image".
 */
export const uploadImage = (appId, file, { altText, environment }) =>
  writeThen(appId, () =>
    axios.post(`${base(appId)}/images`, file, {
      headers: { 'Content-Type': 'image/png' },
      params: { altText, environment },
    }),
  );

export const refreshImageStates = (appId, environment) =>
  writeThen(appId, () => axios.post(`${base(appId)}/images/refresh`, { environment }));

/**
 * Many defaults in one go, reloading once at the end.
 *
 * Sequential because Apple rate-limits, but without the reload `writeThen` does after each write:
 * filling a grid of a hundred cells reloaded the whole page a hundred times. `onProgress` gets the
 * number written so far; a failure stops the run and says how far it got, and pressing again
 * fills only what is still empty.
 */
export const saveDefaults = (appId, payloads, onProgress) => async (dispatch) => {
  dispatch(slice.actions.startSaving());
  let done = 0;
  try {
    for (const payload of payloads) {
      // eslint-disable-next-line no-await-in-loop
      await axios.put(`${base(appId)}/defaults`, payload);
      done += 1;
      onProgress?.(done);
    }
    return null;
  } catch (error) {
    return `${messageOf(error)} — ${done} of ${payloads.length} saved`;
  } finally {
    dispatch(slice.actions.doneSaving());
    await dispatch(getConfiguration(appId));
  }
};

export const saveDefault = (appId, payload) =>
  writeThen(appId, () => axios.put(`${base(appId)}/defaults`, payload));

/** Leaves that product and locale showing nothing at all, which is what Apple does without one. */
export const removeDefault = (appId, defaultId) =>
  writeThen(appId, () => axios.delete(`${base(appId)}/defaults/${defaultId}`));

export const removeRule = (appId, ruleId) =>
  writeThen(appId, () => axios.delete(`${base(appId)}/rules/${ruleId}`));

export function runPreview(appId, payload) {
  return async (dispatch) => {
    dispatch(slice.actions.startPreview());
    try {
      dispatch(slice.actions.previewSuccess(await axios.post(`${base(appId)}/preview`, payload)));
    } catch (error) {
      dispatch(slice.actions.previewFailed(messageOf(error)));
    }
  };
}

export function getRealtimeSetup(appId) {
  return async (dispatch) => {
    dispatch(slice.actions.startRealtime());
    try {
      dispatch(slice.actions.realtimeSuccess(await axios.get(`${base(appId)}/realtime`)));
    } catch (error) {
      dispatch(slice.actions.realtimeFailed(messageOf(error)));
    }
  };
}

/** Writes to the realtime setup reload it, like every other write here reloads its lists. */
const realtimeWrite = (appId, request) => async (dispatch) => {
  dispatch(slice.actions.startSaving());
  try {
    await request();
    dispatch(slice.actions.doneSaving());
    await dispatch(getRealtimeSetup(appId));
    return null;
  } catch (error) {
    dispatch(slice.actions.doneSaving());
    return messageOf(error);
  }
};

/** The URL is derived on the server from the app id; there is nothing to send but the environment. */
export const connectRealtime = (appId, environment) =>
  realtimeWrite(appId, () => axios.put(`${base(appId)}/realtime/${environment}`, {}));

export const startPerformanceTest = (appId, originalTransactionId) =>
  realtimeWrite(appId, () =>
    axios.post(`${base(appId)}/realtime/performance-test`, { originalTransactionId }),
  );
